import { useState } from "react";
import { useFilterState } from "../../ducks/filter/hooks/filterState";
import truncateQuery from "@/utils/truncateQuery";
import {
  FileQuestion,
  ClipboardCopy,
  RefreshCcw,
  PlusCircle,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { QueryEdit } from "./query-edit";
import { useGetFavoritesQuery } from "@/services/it/updates/query-favorites";
import { DataTablePagination } from "./data-table-pagination";
import { Meta } from "@/@types/paginated";
import { QuerySearch } from "./data-table-toolbar";
import SaveNewScript from "./save-new-script";
import { Button } from "components/ui/button";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";

const QueryFavorite = () => {
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const filterState = useFilterState();
  const { filters } = filterState || { filters: {} };
  const { handleModal } = useDialogModal();

  const {
    data: querys,
    isLoading,
    refetch,
  } = useGetFavoritesQuery({
    search: filters.search,
    orderBy: JSON.stringify("desc"),
    perPage: filters.meta.perPage || 4,
    meta: {
      page: filters?.meta?.page || 1,
    },
  });

  const handleCopy = (query: string, id: number) => {
    navigator.clipboard.writeText(query);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1000);
  };

  const hasQueries = querys?.data && querys.data.length > 0;

  const { meta } = querys || { data: [], meta: {} };

  const createFavoriteQuery = () => {
    handleModal({
      isOpen: true,
      element: <SaveNewScript />,
      title: "Criar nova query favorita",
      description: "",
    });
  };

  return (
    <div className="w-full flex flex-col justify-between gap-4">
      <div className="flex items-center justify-between w-full">
        <span className="text-2xl font-bold">Seus Scripts Favoritos</span>
        <button
          onClick={refetch}
          disabled={isLoading}
          className="flex items-center justify-center gap-2 text-black hover:text-black/70 disabled:opacity-50 focus:outline-none"
        >
          <RefreshCcw
            className={`h-3 w-3 ${isLoading ? "animate-spin" : ""}`}
          />
          <span className="text-sm font-medium">
            {isLoading ? "Recarregando..." : "Recarregar"}
          </span>
        </button>
      </div>
      <Card className="w-full">
        <CardContent className="p-6">
          <QuerySearch />
          <div
            className={`w-full flex flex-col gap-4 overflow-auto h-[300px] max-h-[300px] ${
              !hasQueries ? "items-center justify-center" : ""
            }`}
          >
            {hasQueries ? (
              querys.data.map((query) => (
                <div
                  key={query.id}
                  className="w-full rounded-lg bg-gray-50 cursor-pointer border border-gray-200 px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-2 hover:shadow-md transition-shadow duration-200"
                >
                  <div className="flex gap-4 items-center">
                    <span className="font-mono text-gray-700">
                      {query.query_nickname ??
                        truncateQuery(query.query ?? "", 20)}{" "}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {copiedId === query.id && (
                      <p className="text-sm text-[#fff] bg-black px-2 py-1 rounded transition-all ease-in-out duration-300">
                        Copiado
                      </p>
                    )}
                    <button
                      className="text-black bg-[#E9E9EF] hover:bg-[#E9E9EF]/40 px-2 py-2 rounded border transition-all ease-in-out duration-30"
                      onClick={() =>
                        handleCopy(query.query ?? "", Number(query.id))
                      }
                    >
                      <ClipboardCopy size={16} />
                    </button>
                    <QueryEdit
                      queryId={Number(query.id)}
                      query={query.query ?? ""}
                      query_nickname={query.query_nickname}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center text-gray-500 py-8">
                <FileQuestion size={64} className="mb-4 text-gray-400" />
                <p className="text-xl font-semibold">
                  Nenhum script encontrado!
                </p>
                <p className="text-sm mt-2">
                  Seus scripts favoritos aparecerão aqui.
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center justify-end">
            <DataTablePagination meta={meta as Meta} />
          </div>
        </CardContent>
      </Card>
      <Button
        onClick={createFavoriteQuery}
        variant="outline"
        className="w-full"
      >
        <PlusCircle className="mr-2 h-4 w-4" />
        Criar nova query favorita
      </Button>
    </div>
  );
};

export default QueryFavorite;
